import { formatActivationPrice } from "@common/utils/activationSuiteDisplay"
import { formatEuros } from "../../brevet/utils/formatEuros"
import {
    COMMITMENT_COPY,
    FORMAT_KEYS,
    INSCRIPTION_FORMATS,
    START_OPTION_TITLES,
} from "../constants/inscriptionFormats"

/**
 * Ce que l'étape formule décide sans rien dessiner : le prix qu'affiche une
 * tuile, ce qu'une tuile ou un volume a le droit de faire, ce que deviennent
 * format, volume et engagement quand le parent touche l'interrupteur ou une
 * tuile, et les réponses toutes faites de « Début des cours ».
 *
 * Aucun prix n'est calculé ici : ils se LISENT dans la grille servie par le
 * serveur (`config.price_grid`), et la seule formule vendue sans engagement
 * est celle qu'il annonce (`config.no_commitment`).
 *
 * Un « choix » est ce que le parent a composé :
 * `{ offerType, weeklyHours, hoursAllocation, withCommitment, committedFormula }`.
 * `committedFormula` garde la formule engagée qu'il avait avant de couper
 * l'engagement, pour la lui rendre.
 *
 * Les « règles » sont ce qu'un choix doit respecter : `noCommitment`,
 * `priceGrid` et `weeklyHoursByOffer` servis par le serveur, les matières du
 * parcours (`subjectIds`) et la fonction qui répartit un volume entre elles
 * (`splitHours(subjectIds, weeklyHours)`). L'interrupteur et les tuiles
 * rendent un choix COMPLET, bon à écrire tel quel au serveur et au parcours.
 */

/** Un devis sans le champ (devis gravé ou gardé d'avant) est un devis engagé. */
export const quoteIsWithoutCommitment = (quote) =>
    quote?.with_commitment === false

/**
 * L'engagement d'un dossier repris : celui que dit son devis, et sans devis
 * (formule incomplète) celui de ses réponses. Absent, il est engagé.
 */
export const resumedWithCommitment = (checkoutParams, quote) =>
    quote
        ? !quoteIsWithoutCommitment(quote)
        : checkoutParams?.with_commitment !== false

/**
 * Le tarif horaire d'une tuile : nombre français, deux décimales au plus,
 * zéros inutiles retirés (13 → « 13 €/h », 18.778 → « 18,78 €/h »).
 *
 * Ce tarif est INDICATIF : la grille est bâtie sur une mensualité divisée par
 * un volume, et il ne permet pas de retrouver la mensualité à la
 * multiplication près (13,22 × 9 ne fait pas 119). Les montants qui engagent
 * sont les mensualités ; lui sert à comparer, parce que c'est l'unité dans
 * laquelle un parent compare.
 */
export const formatHourlyPrice = (hourlyPrice) => {
    if (hourlyPrice === null || hourlyPrice === undefined) return null
    const value = Number(hourlyPrice)
    if (!Number.isFinite(value)) return null
    return `${String(Number(value.toFixed(2))).replace(".", ",")}\u00a0€/h`
}

/**
 * « 169 € », la mensualité d'un devis. Elle se lit à l'euro : la grille se
 * vend en euros ronds, et les centimes qu'un devis peut porter (21899) ne
 * sont que l'arrondi de son tarif horaire.
 */
export const monthlyPrice = (quote) =>
    formatEuros(
        quote?.monthly_price_cents == null
            ? null
            : Math.round(quote.monthly_price_cents / 100)
    )

/** « 169 €/mois ». */
export const monthlyPriceLabel = (quote) => `${monthlyPrice(quote)}/mois`

/** La ligne de la grille pour cette formule — `null` si elle n'est pas vendue. */
export const priceGridEntry = (
    priceGrid,
    { withCommitment, offerType, weeklyHours }
) =>
    priceGrid?.[withCommitment ? "commitment" : "no_commitment"]?.[offerType]?.[
        weeklyHours
    ] ?? null

/**
 * « Économisez 840 € sur une année » pour cette formule, engagée — `null` tant
 * que la grille ne le dit pas (aucun volume choisi, serveur d'avant).
 */
export const commitmentSavingLabel = (
    priceGrid,
    { offerType, weeklyHours }
) => {
    const cents = priceGridEntry(priceGrid, {
        withCommitment: true,
        offerType,
        weeklyHours,
    })?.yearly_saving_cents
    if (!(cents > 0)) return null
    return COMMITMENT_COPY.saving(formatActivationPrice(cents / 100))
}

const sellsHours = (weeklyHoursByOffer, offerType, weeklyHours) =>
    weeklyHours > 0 &&
    (weeklyHoursByOffer?.[offerType] || []).includes(weeklyHours)

/** Cette répartition décrit-elle la formule : mêmes matières, même total ? */
const allocationFits = (allocation, subjectIds, weeklyHours) => {
    if (!allocation || subjectIds.length === 0) return false
    const keys = Object.keys(allocation)
    return (
        keys.length === subjectIds.length &&
        subjectIds.every((id) => keys.includes(String(id))) &&
        keys.reduce((sum, id) => sum + Number(allocation[id] || 0), 0) ===
            weeklyHours
    )
}

/**
 * Les heures de chaque matière pour ce volume : la répartition qu'on avait
 * (`kept`) si elle décrit encore la formule, sinon des parts égales, et rien
 * quand le volume ne porte pas une heure par matière (suivi de devoirs).
 */
export const allocationFor = (
    kept,
    weeklyHours,
    { subjectIds, splitHours }
) => {
    if (allocationFits(kept, subjectIds, weeklyHours)) return kept
    return weeklyHours > 0 &&
        subjectIds.length > 0 &&
        subjectIds.length <= weeklyHours
        ? splitHours(subjectIds, weeklyHours)
        : {}
}

/** Couper l'engagement : la formule devient celle que le serveur annonce. */
const turnCommitmentOff = (choice, rules) => {
    const { offer_type: offerType, weekly_hours: weeklyHours } =
        rules.noCommitment
    return {
        ...choice,
        withCommitment: false,
        offerType,
        weeklyHours,
        hoursAllocation: allocationFor(null, weeklyHours, rules),
        committedFormula: {
            offerType: choice.offerType,
            weeklyHours: choice.weeklyHours,
            hoursAllocation: choice.hoursAllocation,
        },
    }
}

/**
 * Remettre l'engagement rend le volume choisi avant, si l'offre le vend ; à
 * défaut le volume courant, et aucun si elle ne vend ni l'un ni l'autre (le
 * parent est alors ramené à la question des heures).
 */
const turnCommitmentOn = (choice, offerType, rules) => {
    const before = choice.committedFormula
    const weeklyHours =
        [before?.weeklyHours, choice.weeklyHours].find((hours) =>
            sellsHours(rules.weeklyHoursByOffer, offerType, hours)
        ) ?? 0
    return {
        ...choice,
        withCommitment: true,
        offerType,
        weeklyHours,
        hoursAllocation: allocationFor(
            before?.hoursAllocation,
            weeklyHours,
            rules
        ),
        committedFormula: null,
    }
}

/**
 * L'interrupteur d'engagement. Le remettre rend toute la formule d'avant, son
 * format compris : un parent parti de Solo y revient, il ne se retrouve pas en
 * Tandem sans l'avoir choisi.
 */
export const toggleCommitment = (choice, rules) =>
    choice.withCommitment
        ? turnCommitmentOff(choice, rules)
        : turnCommitmentOn(
              choice,
              choice.committedFormula?.offerType ?? choice.offerType,
              rules
          )

/**
 * La formule que l'engagement vend : celle du choix s'il est engagé, sinon
 * celle que le remettre écrirait — la règle même du geste.
 */
export const engagedFormula = (choice, rules) =>
    choice.withCommitment ? choice : toggleCommitment(choice, rules)

/**
 * Ce que l'engagement fait économiser pour ce choix, coupé ou non : le
 * montant est celui de {@link engagedFormula}. L'interrupteur et la
 * réservation l'annoncent d'ici, pour dire ce qui sera vendu.
 */
export const engagementSavingLabel = (priceGrid, choice, rules) =>
    commitmentSavingLabel(priceGrid, engagedFormula(choice, rules))

/**
 * Toucher une tuile. Sans engagement il n'existe qu'une formule : choisir un
 * autre format remet l'engagement. Engagé, un volume que l'autre offre ne
 * vend pas se décoche. Le format déjà choisi ne change rien : le choix
 * revient tel quel.
 */
export const pickFormat = (choice, offerType, rules) => {
    if (offerType === choice.offerType) return choice
    if (!choice.withCommitment)
        return turnCommitmentOn(choice, offerType, rules)
    const weeklyHours = sellsHours(
        rules.weeklyHoursByOffer,
        offerType,
        choice.weeklyHours
    )
        ? choice.weeklyHours
        : 0
    return {
        ...choice,
        offerType,
        weeklyHours,
        hoursAllocation: allocationFor(
            choice.hoursAllocation,
            weeklyHours,
            rules
        ),
    }
}

/**
 * Le seul volume qu'on peut choisir une fois l'engagement coupé, `null` tant
 * qu'il ne l'est pas. Une seule règle pour griser les volumes ET pour refuser
 * le geste : rien ne doit avoir l'air cliquable sans l'être.
 */
export const hoursLockedTo = (choice, { noCommitment }) =>
    !choice.withCommitment && noCommitment ? noCommitment.weekly_hours : null

/**
 * Les tuiles, dans l'ordre de l'écran. Elles suivent le geste du parent
 * sans attendre de devis : c'est le serveur qui annonce ce qui se vend sans
 * engagement, et un devis peut ne jamais venir tant que la formule est
 * incomplète.
 *
 * Sans engagement, seule l'offre de la formule annoncée se vend : l'autre
 * tuile se grise « Avec engagement », au prix qu'on obtient en la touchant —
 * celui de la formule que {@link pickFormat} rendrait.
 *
 * @returns {Array<{ key: string, status: "available" | "locked", selected: boolean, price: string | null }>}
 */
export const formatTileStates = ({ choice, rules }) =>
    FORMAT_KEYS.map((key) => {
        const { offerType } = INSCRIPTION_FORMATS[key]
        const locked =
            !choice.withCommitment &&
            offerType !== rules.noCommitment?.offer_type
        const priced = locked ? pickFormat(choice, offerType, rules) : choice
        return {
            key,
            status: locked ? "locked" : "available",
            selected: offerType === choice.offerType,
            price: formatHourlyPrice(
                priceGridEntry(rules.priceGrid, { ...priced, offerType })
                    ?.hourly_price
            ),
        }
    })

/**
 * Ce que ce démarrage fait régler, sous sa date. C'est le serveur qui dit si
 * le premier prélèvement tombe ce mois-ci, pas la place de la carte : en fin
 * de mois, « au plus tôt » ne fait régler que la réservation.
 */
const startOptionCaption = (option, quote) => {
    if (option.charged_this_month) {
        return Number.isFinite(option.first_charge_cents) &&
            option.first_charge_on_short_label
            ? `${formatEuros(option.first_charge_cents / 100)} le ${option.first_charge_on_short_label}`
            : null
    }
    return Number.isFinite(quote.deposit_cents) && quote.current_month_label
        ? `${formatEuros(quote.deposit_cents / 100)} seulement en ${quote.current_month_label}`
        : null
}

/**
 * Les réponses toutes faites de « Début des cours », telles que le devis
 * les sert : une ou deux cartes, aucune s'il n'en sert pas (devis gravé ou
 * gardé d'avant). `withAmounts` à faux tait les montants — l'écran ne chiffre
 * rien tant que la formule est incomplète.
 *
 * @returns {Array<{ key: string, startDate: string, title: string, date: string, caption: string | null }>}
 */
export const startOptionCards = (quote, { withAmounts = true } = {}) =>
    Object.keys(START_OPTION_TITLES)
        .map((key) => [key, quote?.start_options?.[key]])
        .filter(([, option]) => option?.start_date)
        .map(([key, option]) => ({
            key,
            startDate: option.start_date,
            title: START_OPTION_TITLES[key],
            date: option.first_session_short_label,
            caption: withAmounts ? startOptionCaption(option, quote) : null,
        }))
